import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { DateInput } from '@/components/core/DateInput';
import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent, Validation } from '../types';

interface DateTimeComponentProps extends EditorComponent {
    // Date specific props go here
    className?: string;
    openCalendarOnMount?: boolean;
}

const DateTimeComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hidden = false,
    readonly = false,
    hideLabel = false,
    gridMode = false,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    handleUrlChange,
    className,
    openCalendarOnMount = false,
}: DateTimeComponentProps) => {

    const [focusDate, setFocusDate] = useState<boolean>(false);

    useFocusOnAttribute<HTMLInputElement>(
        { name: fieldPath },
        setFocusDate
    );

    // Reset focus state when form is being reset (after save)
    const { isResetting } = useFormReset();
    useEffect(() => {
        if (isResetting) {
            setFocusDate(false);
        }
    }, [isResetting]);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));

    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    const placeholder = usePlaceholder(attribute, readonly);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly}
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    name={fieldPath}
                    render={({ field }) =>
                        <DateInput
                            id={fieldPath}
                            borderless={gridMode}
                            gridMode={gridMode}
                            placeholder={placeholder}
                            handleUrlChange={handleUrlChange}
                            disabled={readonly}
                            focusDate={focusDate}
                            openOnMount={openCalendarOnMount}
                            withTime={true}
                            selectedDate={field.value}
                            onChange={value => field.onChange(value || null)}
                            className={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${className}`}
                        />
                    }
                />
            </div>
        </Layout>
    );
};

export { DateTimeComponent };
